import { Button, ClickEvent, DataSourceMode, SlideoutDecorator, TableRow, TableRowDisplayEvent, Textbox, ValidationResult } from "@mcleod/components";
import { AutogenLayoutCarrierLaneRateTable } from "./autogen/AutogenLayoutCarrierLaneRateTable";
import { CarrierLaneRate } from "./CarrierLaneRate";
import { CarrierRate } from "./CarrierRate";
import { ModelRow, StringUtil } from "@mcleod/core";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { RateUtil } from "../RateUtil";


const CRITERIA_FIELDS = ['customer_id', 'order_type_id', 'revenue_code_id', 'carrier_id', 'commodity_id', 'equipment_type_id'];

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    cityType: "C",
    stateType: "S",
    zoneType: "O",
    locationType: "L",
    useDefault: true
}

export class CarrierLaneRateTable extends AutogenLayoutCarrierLaneRateTable {

    private _carrierId = null;
    private _carrierRateId = null;

    onLoad(): void | Promise<void> {
        this.tableCarrierLaneRates.onEditLayoutLoaded = (decorator, tableRow) => {
            const carrierLaneRateLayout = decorator.layout as CarrierLaneRate;
            carrierLaneRateLayout.carrierId = this.carrierId;
            this.setupPriority(carrierLaneRateLayout);
            decorator.multiButton.dropdownItems = null;
            const isSearch = this.mainDataSource.mode === DataSourceMode.SEARCH;
            if (isSearch) {
                decorator.labelTitle.text = 'Search Carrier Lane Rates'
            }
            decorator.multiButton.caption =  (isSearch)
                ? 'Apply Filters'
                : 'Apply Edits';
            decorator.doAfterSlideOut = (decoratorAfterEdit) => {
                this.syncLocationByType(tableRow);
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "origin_type", "origin_city_name", "origin_state", "origin_value");
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "destination_type", "destination_city", "destination_state", "destination_value");
                tableRow.displayComponentValues();
            }
            decorator.onValidate = (panelRow: ModelRow): ValidationResult[] => {
                if (!RateUtil.areValidDates(panelRow.getDate('effective_date'), panelRow.getDate('expiration_date'))) {
                    return [{ isValid: false, validationMessage: 'Effective date must be before expiration date', component: carrierLaneRateLayout.textboxEffectiveDate }];
                }
                return [{ isValid: !RateUtil.isDuplicateRateLane(tableRow.table.allRows, panelRow, true), component: carrierLaneRateLayout.textboxOrigin }]
            }
        }
    }

    buttonSearchOnClick(event: ClickEvent) {
        this.tableCarrierLaneRates.allRows[0].showEditLayout();
    }

    buttonAddOnClick(event: ClickEvent) {
        const saveButton = this.createSaveButton();
        SlideoutDecorator.decorateAndSlideIn('lme/dispatch/rates/CarrierLaneRate', {
            fillVerticalSpace: true,
            width: '75%',
            title: 'Add Carrier Lane Rate',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: saveButton,
            layoutLoadListeners: async (event) => {
                const layout = event.target as CarrierLaneRate;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                this.setupPriority(layout);
                layout.activeRow.addAfterFieldUpdateListener((event) => layout.syncPtToPt());
                layout.syncPtToPt();
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                const carrierLaneRate = decorator.layout as CarrierLaneRate;
                (decorator.layout as CarrierLaneRate).carrierId = this.carrierId;
                saveButton.addClickListener(event => {
                    saveButton.busy = true;
                    decorator.layout.mainDataSource.addValidationListener((event) => {
                        const carrierLaneRateRow = decorator.layout.activeRow;
                        if (!RateUtil.areValidDates(carrierLaneRateRow.getDate('effective_date'), carrierLaneRateRow.getDate('expiration_date'), false)) {
                            const message = 'Effective date must be before expiration date';
                            event.validationResults.push({
                                isValid: false,
                                validationMessage: message,
                                caption: message,
                                component: carrierLaneRate.textboxEffectiveDate
                            });
                        }
                        else {
                            const isDuplicate = RateUtil.isDuplicateRateLane(this.tableCarrierLaneRates.allRows, carrierLaneRateRow, false);
                            if (isDuplicate) {
                                const message = "A target rate is already defined for this lane/commodity/order type/trailer type/revenue with overlapping effective and expiration dates.";
                                event.validationResults.push({
                                    isValid: false,
                                    validationMessage: message,
                                    caption: message,
                                    component: carrierLaneRate.textboxOrigin
                                });
                            }
                        }
                    })
                    if (decorator.layout.mainDataSource.validate()) {
                        const carrierLaneRate = decorator.layout.activeRow;
                        decorator.slideOut();
                        if (!StringUtil.isEmptyString(this.carrierRateId)) {
                            carrierLaneRate.set('carrier_rate_id', this.carrierRateId);
                        }
                        RateUtil.setCountyValuesIfNeeded(carrierLaneRate, "N", "origin_type", "origin_city_name", "origin_state", "origin_value");
                        RateUtil.setCountyValuesIfNeeded(carrierLaneRate, "N", "destination_type", "destination_city", "destination_state", "destination_value");
                        
                        this.tableCarrierLaneRates.addRow(carrierLaneRate, null, { display: true, addToData: true });
                    }
                    saveButton.busy = false;
                });
            }
        });
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        tableRow.showEditLayout();
    }

    createSaveButton() {
        return new Button({
            id: "carrierLaneRateSaveButton",
            caption: "Add",
            backgroundColor: "primary",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        });
    }

    setupPriority(layout: CarrierLaneRate) {
        layout.textboxPriority.enabled = this.shouldAllowPriority();
        if (!layout.textboxPriority.enabled) {
            layout.textboxPriority.disabledTooltip = 'Minimum of two Rate Criteria must be set before priority can be added';
        }
    }

    shouldAllowPriority(): boolean {
        const carrierRate = (this.parent as CarrierRate)?.activeRow;
        return (carrierRate != null)
            ? 2 <= CRITERIA_FIELDS.filter(s => !carrierRate.isNull(s)).length
            : false;
    }

    tableCarrierLaneRatesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.syncLocationByType(tableRow);
    }

    syncLocationByType(tableRow: TableRow) {
        const modelRow = tableRow.data;
        const textboxOrigin = tableRow.findComponentById('textboxOrigin') as Textbox;
        const textboxDestination = tableRow.findComponentById('textboxDestination') as Textbox;

        if (!modelRow.isNull('origin_type') && textboxOrigin != null) {
            const originType = modelRow.getString('origin_type');
            LocationByTypeUtil.setLookupByType(originType, textboxOrigin, false, LOCATION_BY_TYPE_PROPS);
        }
        if (!modelRow.isNull('destination_type') && textboxDestination != null) {
            const destinationType = modelRow.getString('destination_type', null);
            LocationByTypeUtil.setLookupByType(destinationType, textboxDestination, false, LOCATION_BY_TYPE_PROPS);
        }
    }

    get carrierId() {
        return this._carrierId;
    }

    set carrierId(value: string) {
        this._carrierId = value
    }

    public get carrierRateId() {
        return this._carrierRateId;
    }
    public set carrierRateId(value) {
        this._carrierRateId = value;
    }
}
